import React, { useState, useEffect, useMemo } from "react";
import "../styles/product.scss";
import { FaChevronDown, FaInfoCircle, FaShareAlt } from "react-icons/fa";
import { AiOutlineHeart, AiFillHeart } from "react-icons/ai";
import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  EmailShareButton,
  FacebookIcon,
  TwitterIcon,
  WhatsappIcon,
  EmailIcon,
} from "react-share";
import { useDispatch, useSelector } from "react-redux";
import { addToCart } from "../actions/cartActions";
import { useNavigate } from "react-router-dom";
import {
  addToWishlist,
  deleteFromWishlist,
  fetchWishlist,
} from "../actions/wishlistActions";
import CartModal from "./CartModal";
import { useImage } from "../contexts/ImageContext";
import ReviewPage from "./Review";
import formatMoney from "./formatMoney";
import SpecificItemInfo from "./SpecificInfo";
import { $startLoader, $stopLoader } from "../globals";
import OptionBadges from "./OptionBadges";
import { SITE_NAME_FULL } from "../variables";
import { toast } from "react-toastify";

const Product = ({ product, scrollToReviews = false }) => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState("all");
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [addedItem, setAddedItem] = useState(null);
  const [isInWishlist, setIsInWishlist] = useState(false);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedOption, setSelectedOption] = useState("");
  const { getImageUrl } = useImage();
  const [isAddedToCart, setIsAddedToCart] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(product.image);
  const [isSpecificInfoOpen, setIsSpecificInfoOpen] = useState(false);
  const [isShareMenuOpen, setIsShareMenuOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState("");
  const { isAuthenticated } = useSelector((state) => state.auth);
  const [wishList, setWishList] = useState([]);
  const { results = [] } = useSelector((state) => state.wishlist);

  useEffect(() => {
    const getTheCheapestOption = (options) => {
      const cheapestOption = options.reduce((min, option) => {
        return option.additional_price < min.additional_price ? option : min;
      }, options[0]);
      return cheapestOption;
    };
    const checkWishlist = async () => {
      try {
        $startLoader();
        dispatch(fetchWishlist(1));
        if (results) {
          const item = results.find(
            (item) => item.product_id === product.product_id,
          );
          setIsInWishlist(item ? true : false);
          $stopLoader();
        } else {
          $stopLoader();
        }
      } catch (error) {
        $stopLoader();
        console.error("Error checking wishlist:", error);
      }
    };
    if (product.options?.length > 0) {
      setSelectedOption(getTheCheapestOption(product.options));
    } else {
      setSelectedOption("");
    }

    checkWishlist();
  }, [dispatch, product]);

  // Handle scroll to reviews when navigating from order details
  useEffect(() => {
    if (scrollToReviews) {
      setActiveSection("reviews");
      // Add a small delay to ensure the component is fully rendered
      setTimeout(() => {
        const reviewsElement = document.getElementById("reviews");
        if (reviewsElement) {
          reviewsElement.scrollIntoView({ behavior: "smooth" });
        }
      }, 200);
    }
  }, [scrollToReviews]);

  // Close share menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isShareMenuOpen && !event.target.closest(".share-container")) {
        setIsShareMenuOpen(false);
      }
    };

    if (isShareMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isShareMenuOpen]);

  const toggleSection = (section) => {
    setActiveSection(activeSection === section ? null : section);
  };

  const handleWishlistToggle = (e) => {
    e.stopPropagation();

    const item = wishList.find(
      (item) => item.product_id === product.product_id,
    );
    console.log("item", item);
    console.log("wishList", wishList);
    if (item) {
      dispatch(deleteFromWishlist(item.wishlist_id));
    } else {
      dispatch(addToWishlist(product.product_id));
    }
    setIsInWishlist(!isInWishlist);
  };

  const {
    name,
    base_price,
    discounted_price,
    description,
    description_images,
    image,
    other_images = [],
    product_info = [],
    specific_info = [],
    tags = [],
    colors = [],
    sizes = [],
    reviews = [],
    product_rating = 0,
  } = product;

  const sizeChoices = useMemo(
    () =>
      Array.isArray(sizes)
        ? sizes.filter((s) => s && s.size_id && String(s.name || "").trim())
        : [],
    [sizes],
  );

  useEffect(() => {
    setSelectedSize("");
  }, [product.product_id]);

  const selectedSizeData = useMemo(
    () => sizeChoices.find((s) => s.size_id === selectedSize) || null,
    [sizeChoices, selectedSize],
  );

  const sizePriceAdjust = selectedSizeData
    ? Number(selectedSizeData.additional_price || 0) -
      Number(selectedSizeData.discount_price || 0)
    : 0;

  const colorSwatchData = useMemo(() => {
    if (!Array.isArray(colors)) return [];
    return colors
      .map((c) => {
        if (c != null && typeof c === "object") {
          const name = String(c.name || c.label || "").trim();
          if (!name) return null;
          const img = String(c.image || "").trim();
          return { name, image: img || null };
        }
        const s = String(c).trim();
        return s ? { name: s, image: null } : null;
      })
      .filter(Boolean);
  }, [colors]);

  const colorChoices = useMemo(
    () => colorSwatchData.map((x) => x.name),
    [colorSwatchData],
  );

  /** Main image when no color-specific swatch image applies (option image or product hero). */
  const defaultGalleryImage = useMemo(() => {
    if (
      selectedOption &&
      typeof selectedOption === "object" &&
      String(selectedOption.image || "").trim()
    ) {
      return String(selectedOption.image).trim();
    }
    return image;
  }, [selectedOption, image]);

  useEffect(() => {
    if (!selectedColor || colorSwatchData.length === 0) return;
    const entry = colorSwatchData.find((x) => x.name === selectedColor);
    if (!entry) return;
    setSelectedImage(entry.image || defaultGalleryImage);
  }, [selectedColor, colorSwatchData, defaultGalleryImage]);

  const colorPriceAdjust = useMemo(() => {
    if (!selectedColor || !Array.isArray(colors)) return 0;
    const sel = String(selectedColor).trim();
    for (const c of colors) {
      if (c != null && typeof c === "object") {
        const name = String(c.name || c.label || "").trim();
        if (name === sel) {
          return (
            Number(c.additional_price || 0) - Number(c.discount_price || 0)
          );
        }
      } else if (String(c).trim() === sel) {
        return 0;
      }
    }
    return 0;
  }, [colors, selectedColor]);

  useEffect(() => {
    setSelectedColor("");
    setSelectedImage(product.image);
  }, [product.product_id]);

  const discount =
    base_price !== discounted_price
      ? Math.round((1 - discounted_price / base_price) * 100)
      : null;

  const openCartModal = (item) => {
    setAddedItem(item);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const increaseQuantity = () => {
    setQuantity((prevQuantity) => prevQuantity + 1);
  };

  const decreaseQuantity = () => {
    setQuantity((prevQuantity) => Math.max(prevQuantity - 1, 1));
  };

  const handleAddToCart = () => {
    if (!isAuthenticated) {
      navigate(
        `/signin?redirect=${encodeURIComponent(window.location.pathname)}`,
      );
      return;
    }
    if (colorChoices.length && !String(selectedColor).trim()) {
      toast.error("Please select a color.");
      return;
    }
    if (sizeChoices.length && !selectedSize) {
      toast.error("Please select a size.");
      return;
    }
    $startLoader();
    const productToAdd = {
      product_id: product.product_id,
      size_id: sizeChoices.length ? selectedSize : null,
      option_id: selectedOption?.option_id ?? null,
      quantity: quantity,
    };
    if (colorChoices.length) {
      productToAdd.selected_color = selectedColor;
    }

    dispatch(addToCart(productToAdd));
    setIsAddedToCart(true);

    const cartItem = {
      product_image: getImageUrl(product.image),
      product_name: product.name,
      quantity: quantity,
      price: product.discounted_price * quantity,
      selected_color: colorChoices.length ? selectedColor : null,
      selected_size_name: selectedSizeData?.name || null,
    };

    openCartModal(cartItem);
    $stopLoader();
  };

  const handleBuyNow = () => {
    if (colorChoices.length && !String(selectedColor).trim()) {
      toast.error("Please select a color.");
      return;
    }
    if (sizeChoices.length && !selectedSize) {
      toast.error("Please select a size.");
      return;
    }
    $startLoader();
    const orderData = {
      product_id: product.product_id,
      quantity: quantity,
      option_id: selectedOption?.option_id ?? null,
      size_id: sizeChoices.length ? selectedSize : null,
      isDirectBuy: true,
    };
    if (colorChoices.length) {
      orderData.selected_color = selectedColor;
    }

    navigate("/checkout", { state: { orderData } });
    $stopLoader();
  };

  const productUrl = window.location.href;
  const productImage = getImageUrl(image);
  const optionPriceAdjust =
    Number(selectedOption?.additional_price || 0) -
    Number(selectedOption?.discount_price || 0);

  const productPrice =
    discounted_price === "0.00" || discounted_price === 0
      ? formatMoney(
          Number(base_price) +
            optionPriceAdjust +
            sizePriceAdjust +
            colorPriceAdjust,
        )
      : formatMoney(
          Number(discounted_price) +
            optionPriceAdjust +
            sizePriceAdjust +
            colorPriceAdjust,
        );

  const shareTitle = `${name} - ${productPrice}`;
  const shareDescription = `${name} - ${productPrice} at ${SITE_NAME_FULL}`;

  const handleCopyLink = () => {
    const shareText = `${shareTitle} - ${productUrl}`;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(shareText)
        .then(() => {
          alert("Product link copied to clipboard!");
          setIsShareMenuOpen(false);
        })
        .catch(() => {
          prompt("Copy this link:", productUrl);
        });
    } else {
      const textArea = document.createElement("textarea");
      textArea.value = shareText;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand("copy");
        alert("Product link copied to clipboard!");
        setIsShareMenuOpen(false);
      } catch (err) {
        prompt("Copy this link:", productUrl);
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <div className="product-view-container">
      <div className="product-view">
        <div className="product-details-wrapper">
          <div className="product-image-section">
            <div className="product-image-container">
              <div className="thumbnail-images">
                <div
                  className={`thumbnail-item ${
                    image === selectedImage ? "active" : ""
                  }`}
                  onClick={() => setSelectedImage(image)}
                >
                  <img src={getImageUrl(image)} alt={name} />
                </div>
                {other_images &&
                  other_images.map((img, index) => (
                    <div
                      key={index}
                      className={`thumbnail-item ${
                        img === selectedImage ? "active" : ""
                      }`}
                      onClick={() => setSelectedImage(img)}
                    >
                      <img
                        src={getImageUrl(img)}
                        alt={`${name} - ${index + 1}`}
                      />
                    </div>
                  ))}
              </div>
              <div className="main-image">
                <img
                  src={getImageUrl(selectedImage)}
                  alt={name}
                  className="product-image"
                />
              </div>
            </div>
          </div>

          <div className="product-details-section">
            <div className="product-tags">
              {tags.map((tag, index) => (
                <span key={index} className="tag">
                  {tag}
                </span>
              ))}
            </div>

            {selectedOption && selectedOption.name && (
              <div className="selected-option-display">
                <span className="selected-option-label">Selected:</span>
                <span className="selected-option-name">
                  {selectedOption.name}
                </span>
              </div>
            )}

            <h1 className="product-title">{name}</h1>
            <div className="product-rating">
              <span className="stars">★★★★★</span>
              <span className="reviews">
                ({product_rating.toFixed(1)} Reviews)
              </span>
            </div>
            <div className="product-price-section">
              {discounted_price === "0.00" || discounted_price === 0 ? (
                <span className="discounted-price">
                  {formatMoney(
                    Number(base_price) +
                      optionPriceAdjust +
                      sizePriceAdjust +
                      colorPriceAdjust,
                  )}
                </span>
              ) : discounted_price === base_price ? (
                <span className="discounted-price">
                  {formatMoney(
                    Number(discounted_price) +
                      optionPriceAdjust +
                      sizePriceAdjust +
                      colorPriceAdjust,
                  )}
                </span>
              ) : (
                <>
                  <span className="discounted-price">
                    {formatMoney(
                      Number(discounted_price) +
                        optionPriceAdjust +
                        sizePriceAdjust +
                        colorPriceAdjust,
                    )}
                  </span>
                  <span className="original-price">
                    {formatMoney(
                      Number(base_price) +
                        optionPriceAdjust +
                        sizePriceAdjust +
                        colorPriceAdjust,
                    )}
                  </span>
                  <span className="discount">{discount}% Off</span>
                </>
              )}
            </div>
            <p className="delivery-info">We deliver to RWANDA</p>
            {/* <p className="shipping-info">
              Free shipping available from 60,000 RWF
            </p> */}

            {/* {product.options && product.options.length > 0 && (
              <div className="product-options">
                <label htmlFor="product-option">Select Option</label>
                <select
                  id="product-option"
                  className="product-option-dropdown"
                  value={selectedOption}
                  onChange={(e) => setSelectedOption(e.target.value)}
                >
                  <option value="">Select an option</option>
                  {product.options &&
                    product.options.map((option) => (
                      <option key={option.option_id} value={option.option_id}>
                        {option.name}
                      </option>
                    ))}
                </select>
              </div>
            )} */}

            {product.options && product.options.length > 0 && (
              <OptionBadges
                options={product.options}
                value={selectedOption}
                onChange={setSelectedOption}
                selectedOption={selectedOption}
                setSelectedOption={setSelectedOption}
                setSelectedImage={setSelectedImage}
                altImage={image}
                price={
                  product.discounted_price === "0.00" ||
                  product.discounted_price === 0
                    ? product.base_price
                    : product.discounted_price
                }
              />
            )}

            {sizeChoices.length > 0 && (
              <div className="product-options product-size-options">
                <label htmlFor="product-size-choice">Size</label>
                <div
                  className="product-color-swatches"
                  id="product-size-choice"
                  role="listbox"
                  aria-label="Product size"
                >
                  {sizeChoices.map((s) => {
                    const adj =
                      Number(s.additional_price || 0) -
                      Number(s.discount_price || 0);
                    const suffix =
                      adj !== 0
                        ? ` (${adj > 0 ? "+" : ""}${formatMoney(adj)})`
                        : "";
                    return (
                      <button
                        type="button"
                        key={s.size_id}
                        className={`product-color-swatch${
                          selectedSize === s.size_id ? " is-selected" : ""
                        }`}
                        onClick={() => setSelectedSize(s.size_id)}
                        aria-pressed={selectedSize === s.size_id}
                      >
                        {s.name}
                        {suffix}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {colorChoices.length > 0 && (
              <div className="product-options product-color-options">
                <label htmlFor="product-color-choice">Color</label>
                <div
                  className="product-color-swatches"
                  id="product-color-choice"
                  role="listbox"
                  aria-label="Product color"
                >
                  {colorSwatchData.map(({ name: c, image: swatchImg }) => (
                    <button
                      type="button"
                      key={c}
                      className={`product-color-swatch${
                        swatchImg ? " product-color-swatch--thumb" : ""
                      }${selectedColor === c ? " is-selected" : ""}`}
                      onClick={() => setSelectedColor(c)}
                      aria-pressed={selectedColor === c}
                      aria-label={`Color ${c}`}
                    >
                      {swatchImg ? (
                        <>
                          <span className="product-color-swatch__img-wrap">
                            <img
                              src={getImageUrl(swatchImg)}
                              alt=""
                              className="product-color-swatch__img"
                            />
                          </span>
                          <span className="product-color-swatch__label">
                            {c}
                          </span>
                        </>
                      ) : (
                        c
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="product-quantity">
              <button className="quantity-btn" onClick={decreaseQuantity}>
                -
              </button>
              <input
                type="text"
                value={quantity}
                className="quantity-input"
                readOnly
              />
              <button className="quantity-btn" onClick={increaseQuantity}>
                +
              </button>
            </div>

            <div className="product-actions">
              <button className="add-to-cart-btn" onClick={handleAddToCart}>
                {isAddedToCart ? "Order Added Successfully" : "Add to Cart"}
              </button>
              {/* <button className="productbuy-now-btn" onClick={handleBuyNow}>
                Buy Now
              </button> */}
              <button
                className="productbuy-now-btn"
                onClick={handleWishlistToggle}
              >
                {isInWishlist ? (
                  <>
                    <AiFillHeart className="wishlist-icon" />
                    <span>Remove from Wishlist</span>
                  </>
                ) : (
                  <>
                    <AiOutlineHeart className="wishlist-icon" />
                    <span>Add to Wishlist</span>
                  </>
                )}
              </button>
              {/* <div className="wishlist-icon-container">
                {isInWishlist ? (
                  <AiFillHeart
                    className="wishlist-icon"
                    onClick={handleWishlistToggle}
                  />
                ) : (
                  <AiOutlineHeart
                    className="wishlist-icon"
                    onClick={handleWishlistToggle}
                  />
                )}
              </div> */}
            </div>
            <div className="product-share-section">
              <div className="share-container">
                <button
                  className="product-share-btn"
                  onClick={() => setIsShareMenuOpen(!isShareMenuOpen)}
                >
                  <FaShareAlt className="share-icon" />
                  <span>Share Product</span>
                </button>
                {isShareMenuOpen && (
                  <div className="share-menu">
                    <div className="share-menu-header">
                      <h4>Share this product</h4>
                      <button
                        className="share-close-btn"
                        onClick={() => setIsShareMenuOpen(false)}
                      >
                        ×
                      </button>
                    </div>
                    <div className="share-buttons-grid">
                      <FacebookShareButton
                        url={productUrl}
                        quote={shareDescription}
                        hashtag="#KShop"
                        className="share-button-item"
                      >
                        <div className="share-button-content">
                          <FacebookIcon size={32} round />
                          <span>Facebook</span>
                        </div>
                      </FacebookShareButton>

                      <TwitterShareButton
                        url={productUrl}
                        title={shareTitle}
                        hashtags={["KShop", "AsiaShopping"]}
                        className="share-button-item"
                      >
                        <div className="share-button-content">
                          <TwitterIcon size={32} round />
                          <span>Twitter</span>
                        </div>
                      </TwitterShareButton>

                      <WhatsappShareButton
                        url={productUrl}
                        title={shareDescription}
                        separator=" - "
                        className="share-button-item"
                      >
                        <div className="share-button-content">
                          <WhatsappIcon size={32} round />
                          <span>WhatsApp</span>
                        </div>
                      </WhatsappShareButton>

                      <EmailShareButton
                        url={productUrl}
                        subject={shareTitle}
                        body={shareDescription}
                        className="share-button-item"
                      >
                        <div className="share-button-content">
                          <EmailIcon size={32} round />
                          <span>Email</span>
                        </div>
                      </EmailShareButton>

                      <button
                        className="share-button-item copy-link-btn"
                        onClick={handleCopyLink}
                      >
                        <div className="share-button-content">
                          <FaShareAlt className="copy-icon" />
                          <span>Copy Link</span>
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="product-info-section">
        <div
          className="product-info-section-item"
          onClick={() => toggleSection("info")}
        >
          <h2>Product Info</h2>
          <span
            className={`chevron ${activeSection === "info" ? "rotate" : ""}`}
          >
            <FaChevronDown />
          </span>
        </div>
        {(activeSection === "all" || activeSection === "info") && (
          <div className="faq-content">
            <div dangerouslySetInnerHTML={{ __html: description }} />
          </div>
        )}

        <div
          className="product-info-section-item"
          onClick={() => toggleSection("love")}
        >
          <h2>Why We Love It</h2>
          <span
            className={`chevron ${activeSection === "love" ? "rotate" : ""}`}
          >
            <FaChevronDown />
          </span>
        </div>
        {(activeSection === "all" || activeSection === "love") && (
          <div className="faq-content">
            <div
              dangerouslySetInnerHTML={{
                __html:
                  product_info.find((info) => info.label === "Why We Love It")
                    ?.value || "No information available",
              }}
            />
          </div>
        )}

        <div
          className="product-info-section-item"
          onClick={() => toggleSection("features")}
        >
          <h2>Featured Ingredients</h2>
          <span
            className={`chevron ${
              activeSection === "features" ? "rotate" : ""
            }`}
          >
            <FaChevronDown />
          </span>
        </div>
        {(activeSection === "all" || activeSection === "features") && (
          <div className="faq-content">
            <div
              dangerouslySetInnerHTML={{
                __html:
                  product_info.find(
                    (info) => info.label === "Featured Ingredients",
                  )?.value || "No information available",
              }}
            />
          </div>
        )}

        <div
          className="product-info-section-item"
          onClick={() => setIsSpecificInfoOpen(true)}
        >
          <h2>Specific Information</h2>
          <span className="info-icon">
            <FaInfoCircle />
          </span>
        </div>

        {isSpecificInfoOpen && (
          <SpecificItemInfo
            specificInfo={specific_info}
            onClose={() => setIsSpecificInfoOpen(false)}
          />
        )}

        <div
          className="product-info-section-item"
          onClick={() => toggleSection("howToUse")}
        >
          <h2>How to Use</h2>
          <span
            className={`chevron ${
              activeSection === "howToUse" ? "rotate" : ""
            }`}
          >
            <FaChevronDown />
          </span>
        </div>
        {(activeSection === "all" || activeSection === "howToUse") && (
          <div className="faq-content">
            <div
              dangerouslySetInnerHTML={{
                __html:
                  product_info.find((info) => info.label === "How to Use")
                    ?.value || "No information available",
              }}
            />
            {description_images && description_images.length > 0 && (
              <div className="description-images">
                {description_images.map((imageUrl, index) => (
                  <img
                    key={index}
                    src={getImageUrl(imageUrl)}
                    alt={`Usage instruction ${index + 1}`}
                    className="description-image"
                  />
                ))}
              </div>
            )}
          </div>
        )}
        <div
          className="product-info-section-item"
          onClick={() => toggleSection("reviews")}
          id="reviews"
        >
          <h2>Reviews ({reviews.length})</h2>
          <span
            className={`chevron ${activeSection === "reviews" ? "rotate" : ""}`}
          >
            <FaChevronDown />
          </span>
        </div>
        {(activeSection === "all" || activeSection === "reviews") && (
          <div className="faq-content">
            <ReviewPage reviews={reviews} productRating={product_rating} />
          </div>
        )}
      </div>

      <CartModal
        isOpen={isModalOpen}
        closeModal={closeModal}
        cartItem={addedItem}
      />
    </div>
  );
};

export default Product;
